import React, { useEffect, useState } from 'react';
import './App.scss';

import { Client, Provider, cacheExchange, fetchExchange } from 'urql';
import { AuthConfig, authExchange } from '@urql/exchange-auth';
import { sentryExchange } from '@common/sentry/exchange';
import { API_URL_GRAPHQL } from '@common/config/environments';
import { sentryInit } from '@common/sentry/main';
import { globalErrorHandler } from './services/error/globalErrorHandler';
import SentryErrorBoundary from './components/Atoms/ErrorBoundary/SentryErrorBoundary';
import Private from './scenes/Private/Private';
import Public from './scenes/Public/Public';

function App() {

  sentryInit();

  // Initialize global error handlers
  useEffect(() => {
    globalErrorHandler.initialize();
  }, []);

  const [isLogged, setIsLogged] = useState<boolean>(false);

  useEffect(() => {
    setIsLogged(!!localStorage.getItem('token'));

    window.addEventListener("storage", () => {
      setIsLogged(!!localStorage.getItem('token'));
    });

    return () => {
      window.removeEventListener("storage", () => {});
    };
  }, []);

  useEffect(() => {
    const hasSession = document.cookie
      .split(';')
      .some(c => c.trim().startsWith('session_active='));
    
    const hasToken = localStorage.getItem('token');

    if (hasToken && !hasSession) {
      console.warn('⚠️ Session invalid (no session cookie) - forcing logout');
      logout();
    }
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    window.dispatchEvent(new Event("storage"));
    window.location.href = '/';
  }

  const client = new Client({
    url: API_URL_GRAPHQL,
    fetchOptions: {
      credentials: 'include'
    },
    exchanges: [
      cacheExchange,
      sentryExchange,
      authExchange(async (utils: any) => {
        let token = await localStorage.getItem('token');

        const config: AuthConfig = {
          addAuthToOperation(operation) {
            if (!token) return operation;
            return utils.appendHeaders(operation, {
              Authorization: `Bearer ${token}`,
              'Apollo-Require-Preflight': 'true'
            });
          },
          didAuthError(error, _operation) { 
            return error.response?.status === 401 
          },
          async refreshAuth() { 
            logout()
          },
          willAuthError(_operation) { return false }
        }

        return config;
      }),
      fetchExchange,
    ]
  });
  
  return (
    <SentryErrorBoundary>
      <Provider value={client}>
        { isLogged && <Private /> }
        { !isLogged && <Public /> }
      </Provider>
    </SentryErrorBoundary>
  );
}

export default App;
